/** Поле группировки iiko OLAP (как на сервере). */
export const RESTAURANT_GROUP_FIELD = "RestorauntGroup";

const RESTAURANT_GROUP_ALIASES = [
  RESTAURANT_GROUP_FIELD,
  "RestaurantGroup",
  "RestorauntGroup.Id",
  "RestaurantGroup.Id",
];

export function normalizeGroupLabel(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Возвращает значение группы как оно есть у iiko, БЕЗ trim(). Это значение
 * потом уходит обратно на сервер как точный фильтр IncludeValues — если его
 * обрезать здесь, у групп с пробелом в названии (например, "Касса №2 ") фильтр
 * перестаёт совпадать с тем, что реально хранит iiko, и группа всегда отдаёт
 * пустой отчёт. Пробелы не трогаем, только проверяем, что строка не пустая.
 */
export function getRestaurantGroupFromRow(row) {
  if (!row || typeof row !== "object") return null;
  for (const key of RESTAURANT_GROUP_ALIASES) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return String(v);
  }
  for (const key of Object.keys(row)) {
    if (/restora?unt.*group/i.test(key)) {
      const v = row[key];
      if (v != null && String(v).trim() !== "") return String(v);
    }
  }
  return null;
}

export function groupsMatch(selected, rowGroup) {
  if (!selected || !rowGroup) return false;
  return normalizeGroupLabel(selected) === normalizeGroupLabel(rowGroup);
}

export function extractRestaurantGroups(rows) {
  if (!Array.isArray(rows)) return [];
  const set = new Set();
  for (const row of rows) {
    const g = getRestaurantGroupFromRow(row);
    if (g) set.add(g);
  }
  return [...set].sort((a, b) => a.localeCompare(b, "ru"));
}

export function filterRowsByRestaurantGroup(rows, groupName) {
  if (!Array.isArray(rows) || !groupName) return [];
  return rows.filter((row) => groupsMatch(groupName, getRestaurantGroupFromRow(row)));
}

const OLAP_SUM_FIELDS = [
  "DishDiscountSumInt",
  "GuestNum",
  "UniqOrderId",
  "DishReturnSum",
  "OrderItems",
];

const OLAP_AVG_FIELDS = [
  "DishDiscountSumInt.average",
  "DishDiscountSumInt.averageByGuest",
  "ProductCostBase.MarkUp",
  "ProductCostBase.Percent",
];

/** Одна точка на дату (суммируем метрики, если iiko вернул несколько строк на день). */
export function aggregateOlapRowsByDate(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const byDate = new Map();

  for (const row of rows) {
    const dateKey = getOpenDateFromRow(row);
    if (!dateKey) continue;

    if (!byDate.has(dateKey)) {
      byDate.set(dateKey, { ...row, "OpenDate.Typed": dateKey });
      continue;
    }

    const acc = byDate.get(dateKey);
    for (const field of OLAP_SUM_FIELDS) {
      acc[field] = safeNum(acc[field]) + safeNum(row[field]);
    }
    for (const field of OLAP_AVG_FIELDS) {
      acc[field] = safeNum(row[field]);
    }
  }

  return [...byDate.values()].sort((a, b) =>
    String(a["OpenDate.Typed"]).localeCompare(String(b["OpenDate.Typed"])),
  );
}

export function aggregateRowsByField(rows, keyField, sumField = "DishDiscountSumInt") {
  const map = new Map();
  for (const el of rows) {
    if (!el) continue;
    const key = el[keyField] != null && String(el[keyField]).trim() !== "" ? String(el[keyField]) : "—";
    const add = Number(el[sumField]);
    map.set(key, (map.get(key) || 0) + (Number.isNaN(add) ? 0 : add));
  }
  return {
    names: [...map.keys()],
    sums: [...map.values()],
  };
}

export function parseOlapAxisLabels(rows) {
  if (!Array.isArray(rows)) {
    return { openDate: [], openDateIso: [] };
  }
  const openDateIso = rows.map((el) => getOpenDateFromRow(el) || "");
  const openDate = openDateIso.map((iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || "";
    return iso.split("-").reverse().slice(0, 2).join(".");
  });
  return { openDate, openDateIso };
}

export function chartPeriodAxisCaption(openDate, openDateIso, groupByRestaurant, selectedGroup) {
  if (groupByRestaurant && selectedGroup) {
    const labels = Array.isArray(openDate) ? openDate.filter(Boolean) : [];
    if (labels.length === 1) return `${selectedGroup}, ${labels[0]}`;
    if (labels.length > 1) return `${selectedGroup} (${labels.length} дн.)`;
    return selectedGroup;
  }
  const labels = Array.isArray(openDate) ? openDate : [];
  const isoList = Array.isArray(openDateIso) ? openDateIso : [];
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const todayKey = `${today.getFullYear()}-${month}-${day}`;
  const lastIso = isoList.length ? isoList[isoList.length - 1] : "";
  if (lastIso && lastIso === todayKey) return "За сегодня";
  const lastLabel = labels.length ? labels[labels.length - 1] : lastIso;
  if (!lastLabel) return "За сегодня";
  return `За ${lastLabel}`;
}

export function detailsBreakdownTitle(groupByRestaurant) {
  return groupByRestaurant ? "По дням (выбранная группа)" : "По дням";
}

export function detailsBreakdownSubtitle(groupByRestaurant) {
  return groupByRestaurant
    ? "Детализация по дням для выбранной группы ресторанов"
    : "Детализация по дням";
}

export function detailsRowColumnTitle() {
  return "Дата";
}

export function detailsAverageLabel(groupByRestaurant) {
  return groupByRestaurant ? "Среднее за день (группа)" : "Среднее за день";
}

export function safeNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n;
}

/** Дата из строки OLAP (разные форматы iiko). */
export function getOpenDateFromRow(row) {
  if (!row || typeof row !== "object") return null;
  const raw = row["OpenDate.Typed"] ?? row.OpenDate ?? row.openDate;
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
    return s;
  }
  if (typeof raw === "object") {
    const inner = raw.value ?? raw.date ?? raw.Date;
    if (inner != null) return getOpenDateFromRow({ "OpenDate.Typed": String(inner) });
  }
  const s = String(raw).trim();
  return s.length >= 10 ? s.substring(0, 10) : s;
}
